import Stack from "@mui/material/Stack";
import compose from "ramda/src/compose";
import ifElse from "ramda/src/ifElse";
import prop from "ramda/src/prop";
import filter from "ramda/src/filter";
import join from "ramda/src/join";
import length from "ramda/src/length";
import map from "ramda/src/map";
import always from "ramda/src/always";
import identity from "ramda/src/identity";
import PersonIcon from "@atlaskit/icon/glyph/person";
import { usePlacementWorksheetContext } from "../../contexts/PlacementWorksheetContext";
import { usePopupContext } from "../../contexts/PopupContext/context";
import { Radio } from "../../UI/Radio";
import { Typography } from "../../UI/Typography";
import { PlacementWorksheetRightPopupSectionContainer } from "./PlacementWorksheetRightPopupSectionContainer";
import { pathOrArray } from "../../helpers/pathOrArray";
import { flipedGt1 } from "../../helpers/flipedGt1";
import { IPlacementWorksheetUnitOccupant } from "../../services/PlacementWorksheetApi";
import { rejectIsEmpty } from "../../helpers/rejectIsEmpty";
import { IconButton } from "../../UI/IconButton";
import { Box } from "../../UI/Box";
import { GroupFilter } from "./GroupFilter";
import { COLOR_PRIMARY } from "../../constants/styles";
import { usePlacementWorksheetSettingsContext } from "../../contexts/PlacementWorksheetContext";
import { URL } from "../../constants/navigation";
import { useRightPopupContext } from "./context";
import { pathOrString } from "../../helpers/pathOrString";

const SEPARATOR = ",";

const getUnitsOccupants = compose(ifElse(compose(flipedGt1, length), identity, always([])), pathOrArray(["occupants"]));

const getGroups = compose<any, any, any, any>(rejectIsEmpty, map(getUnitsOccupants), pathOrArray(["unassignedUnits"]));

const getIdsString = compose(join(SEPARATOR), map(prop("id")));

export const occupantFilterPredicate = (search?: string) => (occupant: IPlacementWorksheetUnitOccupant) =>
  search
    ? pathOrString(["lastName"], occupant)?.toLowerCase().includes(search.toLowerCase()) ||
    pathOrString(["firstName"], occupant)?.toLowerCase().includes(search.toLowerCase())
    : true;

export const PlacementWorksheetUplacedGroupsSection: React.FC = () => {
  const { occupants } = usePlacementWorksheetContext();
  const { colors } = usePlacementWorksheetSettingsContext();
  const { search, showOnlyRenewing } = useRightPopupContext();
  const { selectedResidents, setSelectedResidents } = usePopupContext();

  // Filter by search term
  const filterBySearch = filter<IPlacementWorksheetUnitOccupant[]>((group) =>
    search ? group.some(occupantFilterPredicate(search)) : true
  );

  // Filter by renewing status
  const filterByRenewing = filter<IPlacementWorksheetUnitOccupant[]>((group) =>
    showOnlyRenewing ? group.some((resident) => resident.status === "renewing") : true
  );

  // Apply both filters
  const groups = occupants ? compose(
    filterByRenewing,
    filterBySearch
  )(getGroups(occupants)) : [];

  const selectedResidentsIds = selectedResidents?.length ? getIdsString(selectedResidents) : '';

  const handleChangeSelectedGroup = (e: React.ChangeEvent<HTMLInputElement>) => {
    const idsArray = e.target.name.split(SEPARATOR);

    // @ts-ignore
    const selectedOccupants = groups?.length ? groups.flat().filter(({ id }: any) => idsArray.includes(id)) : [];

    setSelectedResidents(selectedOccupants);
  };

  const handleViewProfileClick = (residentId: string) => () => {
    if (!residentId) return;

    window.open(URL.USER.replace(":id", residentId), "_blank");
  };

  return (
    <PlacementWorksheetRightPopupSectionContainer title="Matched" subtitle="Select groups to place">
      <Box mb={2}>
        <GroupFilter />
      </Box>
      <Stack direction="column">
        {// @ts-ignore
          groups.map((group: any) => (
            <Radio
              key={getIdsString(group)}
              name={getIdsString(group)}
              isChecked={selectedResidentsIds === getIdsString(group)}
              label={
                <Stack>
                  {group.map((occupant: any) => (
                    <Box display="flex" alignItems="center" key={occupant.id}>
                      <Typography variant="body1">
                        {occupant.firstName} {occupant.lastName}
                      </Typography>
                      {selectedResidentsIds === getIdsString(group) && (
                        <IconButton size="small" sx={{ ml: 0.5 }} onClick={handleViewProfileClick(occupant.id)}>
                          <PersonIcon
                            label="person"
                            size="small"
                            primaryColor={occupant.status === "renewing"
                              ? (colors.renewingResidentColor || COLOR_PRIMARY)
                              : (colors.newResidentColor || COLOR_PRIMARY)}
                          />
                        </IconButton>
                      )}
                    </Box>
                  ))}
                </Stack>
              }
              onChange={handleChangeSelectedGroup}
            />
          ))}
      </Stack>
    </PlacementWorksheetRightPopupSectionContainer>
  );
};
